/**
 * StickyDetailHeader — a sticky title bar for entity detail pages.
 *
 * Stays pinned to the top of the AppShell main content area (just below the
 * AppShell.Header) while the form scrolls beneath. Renders the Back button
 * to the LEFT of the page content margin (negative marginLeft) so the
 * title's start position matches the corresponding list page.
 *
 * Two important positioning details:
 *  1. `top: var(--app-shell-header-height)` docks the row right under the
 *     AppShell header, never behind it.
 *  2. The negative `marginTop` cancels out AppShell.Main's `padding="md"` so
 *     the row sits flush against the bottom of the AppShell header from the
 *     very first paint. Without that adjustment the row would scroll up by
 *     the size of Main's top padding before sticking.
 *
 * Usage:
 *   <StickyDetailHeader
 *     title={...}
 *     backOnClick={() => confirmDiscard(() => navigate(-1))}
 *     primary={<Button form="X-form" type="submit">Save changes</Button>}
 *     destructive={<Button color="red" onClick={...}>Delete</Button>}
 *   />
 */
import { Badge, Box, Button, Group, Title } from '@mantine/core';
import { IconArrowLeft, IconPencil } from '@tabler/icons-react';
import type { ReactNode } from 'react';

export interface StickyDetailHeaderProps {
  title: ReactNode;
  backOnClick: () => void;
  primary?: ReactNode;
  destructive?: ReactNode;
  /**
   * Pedro's UX request (2026-06-10, #21 smoke observation): when the page
   * is in Edit mode, show an explicit "Editing" badge beside the title.
   * Without it, an Edit session with no changes yet (e.g. after unstaging
   * the only staged item) is visually ambiguous with Read mode — the only
   * tell was the header button set. The badge makes the mode legible:
   * "editing, no changes yet" instead of feeling like a zombie state.
   */
  editing?: boolean;
  /** Record-level actions that are not part of Edit mode and work in Read mode too (P34:
   *  Email). Rendered before `primary`; the header sits outside the Read-mode fieldset. */
  actions?: ReactNode;
}

export function StickyDetailHeader({
  title, backOnClick, primary, destructive, editing, actions,
}: StickyDetailHeaderProps) {
  return (
    <Box
      pos="sticky"
      style={{
        top: 'var(--app-shell-header-height, 0)',
        // Cancel AppShell.Main padding-top so the sticky row sits directly
        // under the AppShell header from page load. Same trick on left/right
        // so the divider and surface span the full main content area.
        marginTop: 'calc(var(--mantine-spacing-md) * -1)',
        marginInline: 'calc(var(--mantine-spacing-md) * -1)',
        paddingInline: 'var(--mantine-spacing-md)',
        paddingTop: 'var(--mantine-spacing-sm)',
        paddingBottom: 'var(--mantine-spacing-sm)',
        zIndex: 20,
        backgroundColor: '#ffffff',
        borderBottom: '1px solid var(--mantine-color-cinzento-2)',
      }}
    >
      <Group justify="space-between" align="center" wrap="nowrap">
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={backOnClick}
            style={{ marginLeft: -90, paddingLeft: 4, paddingRight: 8, flexShrink: 0 }}
          >
            Back
          </Button>
          <Title order={2} style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
          }}>{title}</Title>
          {editing && (
            <Badge
              color="blue"
              variant="light"
              size="lg"
              leftSection={<IconPencil size={12} />}
              style={{ flexShrink: 0 }}
            >
              Editing
            </Badge>
          )}
        </Group>
        <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
          {actions}
          {primary}
          {destructive}
        </Group>
      </Group>
    </Box>
  );
}
